import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { fmt, BalanceGraph, AmortTable } from './_finHelpers';

// Mortgage Payoff Calculator — mirrors reference image with sub-calculator
export default function MortgagePayoffCalc() {
  return (
    <CalcShell look="cnet" title="Mortgage Payoff Calculator" subtitle="See how extra payments or biweekly schedules can shorten your mortgage and save interest."
      about={<>
        <p><b>Paying extra</b> on your mortgage principal reduces the total interest paid and shortens the loan term significantly, often by years.</p>
      </>}
      slug="mortgage-payoff"
    >
      <KnownRemainingTerm />
      <hr className="my-8 border-slate-200" />
      <UnknownRemainingTerm />
    </CalcShell>
  );
}

function KnownRemainingTerm() {
  const [amount, setAmount] = useState(400000);
  const [origTerm, setOrigTerm] = useState(30);
  const [rate, setRate] = useState(6);
  const [remYears, setRemYears] = useState(25);
  const [remMonths, setRemMonths] = useState(0);
  const [option, setOption] = useState('extra'); // payoff | extra | biweekly | normal
  const [extraM, setExtraM] = useState(500);
  const [extraY, setExtraY] = useState(0);
  const [oneTime, setOneTime] = useState(0);

  // Sub-calculator inputs — "How much extra do I need to pay to hit target?"
  const [subMode, setSubMode] = useState('years'); // 'years' | 'saveInterest'
  const [targetYears, setTargetYears] = useState(15);
  const [targetSaving, setTargetSaving] = useState(50000);

  const c = useMemo(() => {
    const r = rate/100/12;
    const totalMonths = origTerm * 12;
    const M = r === 0 ? amount/totalMonths : (amount*r*Math.pow(1+r,totalMonths))/(Math.pow(1+r,totalMonths)-1);
    const paidMonths = totalMonths - (remYears*12 + remMonths);
    let bal = amount;
    for (let i = 0; i < paidMonths; i++) { const interest = bal*r; bal = Math.max(0, bal - (M - interest)); }
    const startBalance = bal;

    const simulate = (em, ey, ot, biweekly) => {
      let b = startBalance; let m = 0; let totalI = 0; let cm = 0;
      let payment = M;
      if (biweekly) payment = M * 26 / 12;
      const monthly = [];
      const yearly = [];
      let yPrin = 0, yInt = 0, yPay = 0;
      while (b > 0.01 && m < 1200) {
        m++; cm++;
        const interest = b * r;
        const extra = em + (m === 1 ? ot : 0) + (cm === 12 ? ey : 0);
        // Regular principal + any extra directly reduces balance
        let princTotal = (payment - interest) + extra;
        if (princTotal > b) princTotal = b;
        b = Math.max(0, b - princTotal);
        totalI += interest;
        const paymentTotal = payment + extra;
        yInt += interest; yPrin += princTotal; yPay += paymentTotal;
        monthly.push({ month: m, interest, principal: princTotal, balance: b, payment: paymentTotal });
        if (cm === 12) {
          yearly.push({ year: m/12, interest: yInt, principal: yPrin, balance: b, payment: yPay });
          yInt = 0; yPrin = 0; yPay = 0; cm = 0;
        }
      }
      if (yInt > 0) yearly.push({ year: Math.ceil(m/12), interest: yInt, principal: yPrin, balance: 0, payment: yPay });
      return { months: m, totalI, rows: yearly, payoffM: m, monthlyPay: payment, monthly, yearly };
    };
    const orig = simulate(0, 0, 0, false);
    let scenario = orig;
    if (option === 'extra') scenario = simulate(extraM, extraY, oneTime, false);
    else if (option === 'biweekly') scenario = simulate(0, 0, 0, true);
    else if (option === 'payoff') scenario = { months: 0, totalI: 0, rows: [], payoffM: 0, monthlyPay: startBalance, monthly: [], yearly: [] };

    // Sub-calculator: solve for extra monthly to hit target
    let subExtra = 0;
    if (subMode === 'years') {
      let lo = 0, hi = M * 5;
      for (let i = 0; i < 30; i++) {
        const mid = (lo+hi)/2;
        const s = simulate(mid, 0, 0, false);
        if (s.months > targetYears * 12) lo = mid; else hi = mid;
      }
      subExtra = (lo+hi)/2;
    } else {
      let lo = 0, hi = M * 5;
      for (let i = 0; i < 30; i++) {
        const mid = (lo+hi)/2;
        const s = simulate(mid, 0, 0, false);
        const saved = orig.totalI - s.totalI;
        if (saved < targetSaving) lo = mid; else hi = mid;
      }
      subExtra = (lo+hi)/2;
    }
    return { M, startBalance, orig, scenario, subExtra };
  }, [amount, origTerm, rate, remYears, remMonths, option, extraM, extraY, oneTime, subMode, targetYears, targetSaving]);

  const fmtYM = (m) => `${Math.floor(m/12)} years and ${m%12} months`;
  const interestSaved = c.orig.totalI - c.scenario.totalI;
  const timeSaved = c.orig.months - c.scenario.months;

  return (
    <div>
      <h3 className="text-lg font-bold text-slate-900 mb-1">If you know the remaining loan term</h3>
      <p className="text-sm text-slate-600 mb-4">Use this calculator if the term length of the remaining loan is known and there is information on the original loan — good for new loans or preexisting loans that have never been supplemented with any external payments.</p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Original loan amount</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={amount} onChange={e=>setAmount(+e.target.value||0)} className="flex-1"/></div></div>
          <div className="field"><label>Original loan term</label><div className="flex gap-2"><input type="number" value={origTerm} onChange={e=>setOrigTerm(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">years</span></div></div>
          <div className="field"><label>Interest rate</label><div className="flex gap-2"><input type="number" step="0.01" value={rate} onChange={e=>setRate(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
          <div className="field"><label>Remaining term</label>
            <div className="grid grid-cols-2 gap-2">
              <div className="flex gap-2"><input type="number" value={remYears} onChange={e=>setRemYears(+e.target.value||0)}/><span className="self-center text-slate-500">years</span></div>
              <div className="flex gap-2"><input type="number" value={remMonths} onChange={e=>setRemMonths(+e.target.value||0)}/><span className="self-center text-slate-500">months</span></div>
            </div>
          </div>
          <div className="mt-4 space-y-2">
            <label className="flex items-center gap-2 text-sm"><input type="radio" name="opt" checked={option==='payoff'} onChange={()=>setOption('payoff')}/> Payback altogether</label>
            <label className="flex items-start gap-2 text-sm"><input type="radio" name="opt" checked={option==='extra'} onChange={()=>setOption('extra')}/>
              <div className="flex-1">
                <div className="font-medium mb-1">Repayment with extra payments</div>
                <div className={`p-3 rounded ${option==='extra'?'border border-blue-400 bg-blue-50/40':'border border-slate-200'}`}>
                  <div className="grid grid-cols-[1fr_auto] gap-2 text-xs items-center">
                    <div className="flex items-center gap-1"><span>Extra</span><span className="text-slate-500">$</span><input type="number" value={extraM} onChange={e=>setExtraM(+e.target.value||0)} className="!py-1 flex-1 border border-slate-300 rounded px-1"/></div><span>per month</span>
                    <div className="flex items-center gap-1"><span>Extra</span><span className="text-slate-500">$</span><input type="number" value={extraY} onChange={e=>setExtraY(+e.target.value||0)} className="!py-1 flex-1 border border-slate-300 rounded px-1"/></div><span>per year</span>
                    <div className="flex items-center gap-1"><span>Extra</span><span className="text-slate-500">$</span><input type="number" value={oneTime} onChange={e=>setOneTime(+e.target.value||0)} className="!py-1 flex-1 border border-slate-300 rounded px-1"/></div><span>one time</span>
                  </div>
                </div>
              </div>
            </label>
            <label className="flex items-center gap-2 text-sm"><input type="radio" name="opt" checked={option==='biweekly'} onChange={()=>setOption('biweekly')}/> Biweekly repayment</label>
            <label className="flex items-center gap-2 text-sm"><input type="radio" name="opt" checked={option==='normal'} onChange={()=>setOption('normal')}/> Normal repayment</label>
          </div>
        </div>
        <div>
          <div className="result-panel mb-3"><div className="result-title">Payoff in</div><div className="result-value" data-testid="payoff-time">{fmtYM(c.scenario.months)}</div></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 bg-green-50 rounded-lg border border-green-200"><div className="text-xs text-slate-600">Interest savings</div><div className="text-xl font-bold text-green-700">{fmt(Math.max(0, interestSaved))}</div></div>
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200"><div className="text-xs text-slate-600">Time savings</div><div className="text-xl font-bold text-blue-700">{fmtYM(Math.max(0,timeSaved))}</div></div>
          </div>
          <table className="data-table mt-3"><thead><tr><th></th><th className="text-right">Original</th><th className="text-right">With payoff</th></tr></thead><tbody>
            <tr><td>Monthly pay</td><td className="text-right">{fmt(c.M)}</td><td className="text-right">{fmt(c.scenario.monthlyPay)}</td></tr>
            <tr><td>Total interest</td><td className="text-right">{fmt(c.orig.totalI)}</td><td className="text-right">{fmt(c.scenario.totalI)}</td></tr>
            <tr><td>Payoff in</td><td className="text-right">{fmtYM(c.orig.months)}</td><td className="text-right font-semibold">{fmtYM(c.scenario.months)}</td></tr>
          </tbody></table>
        </div>
      </div>

      {/* Amortization table + balance graph — inside tool area */}
      {c.scenario.yearly && c.scenario.yearly.length > 0 && (
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <h3 className="font-bold text-slate-900 mb-3">Amortization Schedule (with payoff strategy)</h3>
            <AmortTable schedule={c.scenario} />
          </div>
          <div>
            <h3 className="font-bold text-slate-900 mb-3">Balance / Interest / Payment</h3>
            <BalanceGraph yearly={c.scenario.yearly} startBalance={c.startBalance}/>
          </div>
        </div>
      )}

      {/* Sub-Calculator: how much extra to hit target */}
      <div className="mt-8 rounded-xl border border-slate-200 overflow-hidden">
        <div className="bg-blue-800 text-white px-4 py-2 font-bold">Extra Payment Solver — how much do I need to pay?</div>
        <div className="p-5">
          <div className="flex flex-wrap items-center gap-3 text-sm mb-4">
            <label className="flex items-center gap-2"><input type="radio" checked={subMode==='years'} onChange={()=>setSubMode('years')}/> Pay off in</label>
            <input type="number" value={targetYears} onChange={e=>setTargetYears(+e.target.value||0)} className="w-20 border border-slate-300 rounded px-2 py-1"/><span>years</span>
            <span className="mx-2 text-slate-400">OR</span>
            <label className="flex items-center gap-2"><input type="radio" checked={subMode==='saveInterest'} onChange={()=>setSubMode('saveInterest')}/> Save</label>
            <div className="flex items-center border border-slate-300 rounded px-2 py-1"><span className="text-slate-500">$</span><input type="number" value={targetSaving} onChange={e=>setTargetSaving(+e.target.value||0)} className="w-24 outline-none px-1"/></div><span>in interest</span>
          </div>
          <div className="text-slate-800">
            Required extra monthly payment: <b className="text-blue-700 text-lg" data-testid="mp-sub-extra">{fmt(c.subExtra)}</b> / month
          </div>
        </div>
      </div>

      <StepsPanel steps={[
        `Original monthly P&I = ${fmt(c.M)}`,
        `Current remaining balance ≈ ${fmt(c.startBalance)}`,
        option === 'biweekly' ? `Biweekly payments equate to ${fmt(c.scenario.monthlyPay)}/mo (26 payments/yr ÷ 12)` : `Extra ${fmt(extraM)}/mo + ${fmt(extraY)}/yr + one-time ${fmt(oneTime)}`,
        `Interest saved = original interest − scenario interest = <b>${fmt(Math.max(0, interestSaved))}</b>`,
        `Loan paid off ${timeSaved > 0 ? `<b>${fmtYM(timeSaved)}</b> sooner` : 'at the original schedule'}`
      ]}/>
    </div>
  );
}

/* -------- If you don't know the remaining loan term -------- */
function UnknownRemainingTerm() {
  const [unpaidBal, setUnpaidBal] = useState(230000);
  const [monthlyPmt, setMonthlyPmt] = useState(1500);
  const [rate, setRate] = useState(6);
  const [option, setOption] = useState('extra'); // extra | biweekly | normal
  const [extraM, setExtraM] = useState(500);
  const [extraY, setExtraY] = useState(0);
  const [oneTime, setOneTime] = useState(0);

  const c = useMemo(() => {
    const r = rate / 100 / 12;
    // Compute remaining term from balance & monthly payment: n = -ln(1 - r*B/M) / ln(1+r)
    let origMonths = 0;
    if (r === 0) origMonths = unpaidBal / monthlyPmt;
    else {
      const val = 1 - (r * unpaidBal) / monthlyPmt;
      origMonths = val > 0 ? -Math.log(val) / Math.log(1 + r) : 1200;
    }
    origMonths = Math.min(1200, Math.max(1, origMonths));

    const simulate = (em, ey, ot, biweekly) => {
      let b = unpaidBal, m = 0, totalI = 0, cm = 0;
      let pmt = biweekly ? monthlyPmt * 26 / 12 : monthlyPmt;
      const yearly = [];
      let yInt = 0, yPrin = 0, yPay = 0;
      while (b > 0.01 && m < 1200) {
        m++; cm++;
        const interest = b * r;
        const extra = em + (m === 1 ? ot : 0) + (cm === 12 ? ey : 0);
        let princTotal = (pmt - interest) + extra;
        if (princTotal > b) princTotal = b;
        b = Math.max(0, b - princTotal);
        totalI += interest;
        yInt += interest; yPrin += princTotal; yPay += (pmt + extra);
        if (cm === 12) { yearly.push({ year: m/12, interest: yInt, principal: yPrin, balance: b, payment: yPay }); yInt=0; yPrin=0; yPay=0; cm=0; }
      }
      if (yInt > 0) yearly.push({ year: Math.ceil(m/12), interest: yInt, principal: yPrin, balance: 0, payment: yPay });
      return { months: m, totalI, monthlyPay: pmt, yearly };
    };
    const orig = simulate(0, 0, 0, false);
    let scenario = orig;
    if (option === 'extra') scenario = simulate(extraM, extraY, oneTime, false);
    else if (option === 'biweekly') scenario = simulate(0, 0, 0, true);
    return { orig, scenario, origMonths };
  }, [unpaidBal, monthlyPmt, rate, option, extraM, extraY, oneTime]);

  const fmtYM = (m) => `${Math.floor(m/12)} years and ${Math.round(m%12)} months`;
  const interestSaved = c.orig.totalI - c.scenario.totalI;
  const timeSaved = c.orig.months - c.scenario.months;

  return (
    <div>
      <h3 className="text-lg font-bold text-slate-900 mb-1">If you don't know the remaining loan term</h3>
      <p className="text-sm text-slate-600 mb-4">Use this calculator if the term length of the remaining loan is not known. The unpaid principal balance, interest rate, and monthly payment values can be found in the monthly or quarterly mortgage statement.</p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Unpaid principal balance</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={unpaidBal} onChange={e=>setUnpaidBal(+e.target.value||0)} className="flex-1" data-testid="mp2-balance"/></div></div>
          <div className="field"><label>Monthly payment</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={monthlyPmt} onChange={e=>setMonthlyPmt(+e.target.value||0)} className="flex-1" data-testid="mp2-payment"/></div></div>
          <div className="field"><label>Interest rate</label><div className="flex gap-2"><input type="number" step="0.01" value={rate} onChange={e=>setRate(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
          <div className="mt-4 space-y-2">
            <label className="flex items-start gap-2 text-sm"><input type="radio" name="opt2" checked={option==='extra'} onChange={()=>setOption('extra')}/>
              <div className="flex-1">
                <div className="font-medium mb-1">Repayment with extra payments</div>
                <div className={`p-3 rounded ${option==='extra'?'border border-blue-400 bg-blue-50/40':'border border-slate-200'}`}>
                  <div className="grid grid-cols-[1fr_auto] gap-2 text-xs items-center">
                    <div className="flex items-center gap-1"><span>Extra</span><span className="text-slate-500">$</span><input type="number" value={extraM} onChange={e=>setExtraM(+e.target.value||0)} className="!py-1 flex-1 border border-slate-300 rounded px-1"/></div><span>per month</span>
                    <div className="flex items-center gap-1"><span>Extra</span><span className="text-slate-500">$</span><input type="number" value={extraY} onChange={e=>setExtraY(+e.target.value||0)} className="!py-1 flex-1 border border-slate-300 rounded px-1"/></div><span>per year</span>
                    <div className="flex items-center gap-1"><span>Extra</span><span className="text-slate-500">$</span><input type="number" value={oneTime} onChange={e=>setOneTime(+e.target.value||0)} className="!py-1 flex-1 border border-slate-300 rounded px-1"/></div><span>one time</span>
                  </div>
                </div>
              </div>
            </label>
            <label className="flex items-center gap-2 text-sm"><input type="radio" name="opt2" checked={option==='biweekly'} onChange={()=>setOption('biweekly')}/> Biweekly repayment</label>
            <label className="flex items-center gap-2 text-sm"><input type="radio" name="opt2" checked={option==='normal'} onChange={()=>setOption('normal')}/> Normal repayment</label>
          </div>
        </div>
        <div>
          <div className="result-panel mb-3"><div className="result-title">Payoff in</div><div className="result-value" data-testid="mp2-payoff-time">{fmtYM(c.scenario.months)}</div></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 bg-green-50 rounded-lg border border-green-200"><div className="text-xs text-slate-600">Interest savings</div><div className="text-xl font-bold text-green-700">{fmt(Math.max(0, interestSaved))}</div></div>
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200"><div className="text-xs text-slate-600">Time savings</div><div className="text-xl font-bold text-blue-700">{fmtYM(Math.max(0, timeSaved))}</div></div>
          </div>
          <table className="data-table mt-3">
            <thead><tr><th></th><th className="text-right">Original</th><th className="text-right">With payoff</th></tr></thead>
            <tbody>
              <tr><td>Remaining term</td><td className="text-right">{fmtYM(c.orig.months)}</td><td className="text-right font-semibold">{fmtYM(c.scenario.months)}</td></tr>
              <tr><td>Total payments</td><td className="text-right">{fmt(c.orig.months * monthlyPmt)}</td><td className="text-right">{fmt(c.scenario.months * c.scenario.monthlyPay + (option==='extra' ? extraM * c.scenario.months + extraY * Math.floor(c.scenario.months/12) + oneTime : 0))}</td></tr>
              <tr><td>Total interest</td><td className="text-right">{fmt(c.orig.totalI)}</td><td className="text-right">{fmt(c.scenario.totalI)}</td></tr>
            </tbody>
          </table>
        </div>
      </div>
      {c.scenario.yearly && c.scenario.yearly.length > 0 && (
        <div className="mt-6">
          <h4 className="font-bold text-slate-900 mb-3">Balance / Interest / Payment</h4>
          <BalanceGraph yearly={c.scenario.yearly} startBalance={unpaidBal}/>
        </div>
      )}
    </div>
  );
}
